import { defineStore } from 'pinia'
import { computed, ref, type ComputedRef } from 'vue'
import type {
  ConnectPartnerConfigResponse,
  PartnerContent,
  SelectedDeviceClassDto,
  Translations
} from '@/libs/api/connect/connect.types.ts'
import { useConnectDataAttributes } from '@/libs/api/connect/connect.composables.ts'
import { useConnectApiClientImpl } from '@/client/connect-client-impl.ts'
import { language } from '@/i18n/i18n.ts'
import { DEFAULT_LOCALE as DEFAULT_LOCALE_I18N } from '@/ts/constants/constants'
import { DEFAULT_LOCALE } from './constants'

export const useConnectStore = defineStore('connect', () => {
  const _dataAttributes = useConnectDataAttributes()

  const client = useConnectApiClientImpl()

  const _configResponse = ref<ConnectPartnerConfigResponse>()

  async function loadPartnerConfig() {
    _configResponse.value = await client.getPartnerConfig()
    await partnerTracking()
  }

  const dataAttributes = computed(() => {
    return _dataAttributes
  })

  const partnerName = computed(() => {
    return _configResponse.value?.partner_name
  })

  const getTranslations = computed(() => {
    return _configResponse.value?.bootstrap.i18n as Record<string, Translations>
  })

  const getOverrideTranslations = computed(() => {
    return _configResponse.value?.config.i18n as Record<string, Translations>
  })

  const getDeviceIds = computed(() => {
    return _configResponse.value?.bootstrap.config.constants
  })

  const getLinexoSettings = computed(() => {
    return _configResponse.value?.config.settingsLinexo
  })

  const getConnectVariant = computed(() => {
    return _configResponse.value?.config.settingsLinexo?.siteConnectVariant
  })

  const getSelectedDeviceClasses = computed(() => {
    const selected = _configResponse.value?.config.selectedDeviceClasses
    return selected?.map((it) => {
      return {
        id: it.id,
        name: it.translate,
        keyText: it.translate,
        icon: it.svg
      } as SelectedDeviceClassDto
    })
  })

  const getSignets = computed(() => {
    const locale = dataAttributes.value.locale || DEFAULT_LOCALE
    const languageKey = language(locale)
    return _configResponse.value?.bootstrap.bikeSignets[languageKey]
  })

  const linexoConstants = computed(() => {
    return _configResponse.value?.bootstrap.config.linexo?.constants
  })

  const batteryDeviceId = computed(() => {
    if (typeof linexoConstants.value === 'object' && linexoConstants.value !== null) {
      return linexoConstants.value['DEVICE_TYPE_BATTERY']?.id || ''
    }
    return ''
  })

  const devicesWithBatteryOffer = computed(() => {
    if (typeof linexoConstants.value === 'object' && linexoConstants.value !== null) {
      return linexoConstants.value['DEVICE_TYPES_WITH_BATTERY_OFFER']?.ids
    }
    return []
  })

  const getPartnerContent: ComputedRef<PartnerContent | undefined> = computed(() => {
    const locale = dataAttributes.value.locale || DEFAULT_LOCALE
    const languageKey = language(locale)
    return _configResponse.value?.partner_content?.[languageKey]
  })

  const theme = computed(() => _configResponse.value?.config.theme)

  const hasAccumulatorOffer = (deviceClassId: string) => {
    return devicesWithBatteryOffer.value?.includes(deviceClassId)
  }

  const partnerTracking = async () => {
    const url = window.location.href
    const lang = dataAttributes.value.locale || DEFAULT_LOCALE_I18N
    const brokerNumber = dataAttributes.value.brokerNumber
    const version = __APP_VERSION__

    if (!!url && !!lang && !!brokerNumber && !!version)
      try {
        await client.partnerTracking({ url, lang, broker_number: brokerNumber ?? '', version })
      } catch (error) {
        console.error('Partner Tracking error', error)
      }
  }

  return {
    _configResponse,
    dataAttributes,
    loadPartnerConfig,
    getTranslations,
    getOverrideTranslations,
    getDeviceIds,
    getSelectedDeviceClasses,
    partnerName,
    getLinexoSettings,
    getConnectVariant,
    getPartnerContent,
    getSignets,
    linexoConstants,
    batteryDeviceId,
    devicesWithBatteryOffer,
    theme,
    hasAccumulatorOffer
  }
})
